import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    TextField,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    Skeleton,
    LinearProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { styled } from '@mui/material/styles';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { authenticatedPostRequest } from '../RestApi/RestApi';
import { brand, gray, green } from '../getTheme/getTheme';
import SketchedLogoSpinner from '../Spinner/Spinner';


// 'Referral Program Banner Style:
const GradientText = styled(Typography)(({ theme }) => ({
    background: `linear-gradient(45deg, ${green[300]} 15%, ${brand[700]} 90%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 'bold',
}));


// These cards show the referral codes.
// Note that these colors aren't in our branding, so they are explicitly defined. 
// Redeemed is faint blue, claimed is yellow, eligible is green.
const ReferralCodeCard = styled(Card)(({ theme, claimed, redeemed }) => ({
    background: 
        redeemed? 'linear-gradient(135deg, rgba(100, 181, 246, 0.2) 0%, rgba(33, 150, 243, 0.3) 100%)'
        :claimed? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.2) 100%)'
        :'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': { transform: 'translateY(-5px)',    
    },
}));


// Within the Eligible Rewards section, this box will hold the pills for each reward.
const RewardBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    background: `linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(33, 150, 243, 0.2) 100%)`,
    marginBottom: theme.spacing(3),
}));


// This pill used to display each reward.
const RewardPill = styled(Chip)(({ theme, iseligible }) => ({
    background: iseligible 
        ? 'linear-gradient(135deg, rgba(0, 188, 212, 0.3) 0%, rgba(0, 229, 255, 0.3) 100%)'
        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(33, 150, 243, 0.3) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: theme.palette.text.primary,
    fontWeight: 500,
    margin: theme.spacing(0.5),
    cursor: iseligible ? 'pointer' : 'default',
    '&:hover': iseligible ? {
        background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.4) 0%, rgba(0, 229, 255, 0.4) 100%)',
        transform: 'translateY(-1px)'
    } : {}
}));


// This is the main component for the referral program.
export function Coupon() {
    const { route, user } = useAuthenticator((context) => [context.route, context.user]);

    // We'll use this for displaying errors and status messages
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Coupon codes assigned and available to give out; also those claimed by this user to track
    // what rewards they've earned. 
    const [coupons, setCoupons] = useState([]);
    const [claimedCoupons, setClaimedCoupons] = useState([]);


    const [loading, setLoading] = useState(true);
    
    // Dialog for claiming an invitation code manually in case it didn't come through
    const [openClaimDialog, setOpenClaimDialog] = useState(false);
    const [invitationCode, setInvitationCode] = useState('');

    // Loading states for the various actions
    const [claimLoading, setClaimLoading] = useState(false); // claiming an invitation code
    const [isRedeeming, setIsRedeeming] = useState(false);   // claiming a referrer reward
    const [generating, setGenerating] = useState(false);     // generating referral codes

    // Whether the user is eligible for the referrer reward
    const [isEligibleForReferralReward, setIsEligibleForReferralReward] = useState(false);
    
    
    // Primary useEffect: Fetch the various coupons associated with a user to drive the UI
    useEffect(() => {

        const fetchCoupons = async () => {
        if (route === 'authenticated') {
            
            try {        

            // Fetch assigned coupons
            await refreshAssignedCoupons();
                        
            // Fetch claimed coupons as well
            await refreshClaimedCoupons();
            
            } catch (err) {

            setSnackbarMessage('Failed to load your coupons. Please try again later.');
            setOpenSnackbar(true);
            console.log("Error fetching coupons:", err);

            }

            setLoading(false);
        }
        };

        if (user?.signInUserSession?.idToken?.payload?.sub) {
        fetchCoupons();
        }

    }, [user, route]);


    // Recompute eligibility for referral reward whenever coupons changes
    useEffect(() => {
        const redeemedCount = coupons.filter(coupon => 
        coupon.redeemed_at && coupon.claimed_by_sub
        ).length;
        setIsEligibleForReferralReward(redeemedCount >= 3);
        console.log("redeemedCount", redeemedCount, isEligibleForReferralReward);
    }, [coupons]);

    // When snackbar opens, set a timer to close it after 6 seconds
    useEffect(() => {
        let timer;
        if (openSnackbar) {
        timer = setTimeout(() => {
            setOpenSnackbar(false);
        }, 6000);
        }
        return () => clearTimeout(timer);
    }, [openSnackbar]);



    // Handle pressing the button to copy a referral link to the clipboard
    const handleCopyLink = async (code) => {
        const referralLink = `${process.env.REACT_APP_WWW_URL}/signup?invitation_code=${code}`;
        try {
        await navigator.clipboard.writeText(referralLink);
        setSnackbarMessage('Referral link copied to clipboard!');
        setOpenSnackbar(true);
        } catch (err) {
        setSnackbarMessage('Failed to copy link');
        setOpenSnackbar(true);
        }
    };

    // Handle pressing the button to share a referral link
    const handleShare = async (code) => {
        const referralLink = `${process.env.REACT_APP_WWW_URL}/signup?invitation_code=${code}`;
        if (navigator.share) {
        try {
            await navigator.share({
            title: 'Join me on Revelata',
            text: 'Check out Revelata - This referral code gets you a month free access to the full platform.',
            url: referralLink,
            });
            setSnackbarMessage('Thanks for sharing!');
            setOpenSnackbar(true);
        } catch (err) {
            if (err.name !== 'AbortError') {
            setSnackbarMessage('Failed to share');
            setOpenSnackbar(true);
            }
        }
        } else {
        await handleCopyLink(code);
        }
    };


    // When someone has manually entered an invitation code -- e.g. if there 
    // was a problem and it wasn't auto claimed, then let's send it off to the
    // server
    const handleClaimCode = async () => {
        if (!invitationCode.trim()) {
        return;
        }

        // To disable the button while we wait for the server to process the claim
        setClaimLoading(true);

        // Close the dialog and clear the input field immediately
        setOpenClaimDialog(false);
        setInvitationCode('');

        authenticatedPostRequest(
            `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/coupons/claim`,
            { 
                couponCode: invitationCode.trim(),
                cognitoSub: user.signInUserSession.idToken.payload.sub
            }

        ).then(response => {
            setSnackbarMessage('Invitation code claimed successfully!');
            setOpenSnackbar(true);

        }).catch(async (err) => {
            setSnackbarMessage(err.message || 'Failed to claim invitation code');
            setOpenSnackbar(true);

        }).finally(() => {
            setClaimLoading(false);
            refreshClaimedCoupons();
        });

    };

    // Handle pressing the button to redeem the referrer reward, which is available once
    // all three referral codes have been redeemed.
    const handleRedeemReferrerCoupon = async () => {
        if (!isEligibleForReferralReward || isRedeeming) return;
        
        setIsRedeeming(true);
        try {
        await authenticatedPostRequest(
            `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/coupons/redeem-referral`,
            { 
            cognitoSub: user.signInUserSession.idToken.payload.sub,
            campaignId: process.env.REACT_APP_REFERRAL_CAMPAIGN_ID
            }
        );
        
        setSnackbarMessage('Referral reward redeemed successfully! Your free month has been applied.');
        setOpenSnackbar(true);

        await refreshClaimedCoupons();      
        } catch (err) {
        setSnackbarMessage(err.message || 'Failed to redeem referral reward');
        setOpenSnackbar(true);
        } finally {
        setIsRedeeming(false);
        }
    };

    // Handle pressing the button to generate referral codes
    const handleGenerateReferralCodes = useCallback(async () => {
        if (!user?.signInUserSession?.idToken?.payload?.sub) return;

        // To disable the button while we wait for the server to process the request
        setGenerating(true);
        try {
        await authenticatedPostRequest(
            `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/coupons/assign`,
            { cognitoSub: user.signInUserSession.idToken.payload.sub, 
            campaignId: process.env.REACT_APP_REFERRAL_CAMPAIGN_ID
            }
        );
        setSnackbarMessage('Referral codes generated!');
        setOpenSnackbar(true);

        // Refresh coupons list
        await refreshAssignedCoupons();

        } catch (err) {
        setSnackbarMessage(err.message || 'Failed to generate referral codes');
        setOpenSnackbar(true);
        } finally {
        setGenerating(false);
        }
    }, [user]);



    
    // Check if user has already claimed a referral reward for this campaign
    const hasClaimedReferralReward = claimedCoupons.some(
        (coupon) => coupon.campaign_id === process.env.REACT_APP_REFERRAL_CAMPAIGN_ID
    );

    // Check if user has already claimed a referrER reward for this campaign
    const hasClaimedReferrerReward = claimedCoupons.some(
        (coupon) => coupon.campaign_id === process.env.REACT_APP_REFERRER_CAMPAIGN_ID
    );

    // Track redeemedCount for use in rendering
    const redeemedCount = coupons.filter(coupon => 
        coupon.redeemed_at && coupon.claimed_by_sub
    ).length;

    
    // Helper to refresh claimed coupons
    const refreshClaimedCoupons = useCallback(async () => {
        if (!user?.signInUserSession?.idToken?.payload?.sub) return;
        const claimedResponse = await authenticatedPostRequest(
        `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/coupons/get-claimed`,
        { cognitoSub: user.signInUserSession.idToken.payload.sub }
        );
        setClaimedCoupons(claimedResponse.coupons || []);
    }, [user]);

    
    // When dialog closes (cancel or after claim), refresh claimed coupons
    const handleCloseClaimDialog = useCallback(() => {
        setOpenClaimDialog(false);
        setInvitationCode('');
        refreshClaimedCoupons();
    }, [refreshClaimedCoupons]);


    // Helper to refresh assigned coupons
    const refreshAssignedCoupons = useCallback(async () => {
        if (!user?.signInUserSession?.idToken?.payload?.sub) return;
        const response = await authenticatedPostRequest(
        `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/coupons/get-assigned`,
        { cognitoSub: user.signInUserSession.idToken.payload.sub }
        );
        setCoupons(response.coupons);
    }, [user]);


    return (

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Title and subtitle text */}
            <GradientText variant="h3" component="h1" gutterBottom>
            Referral Rewards
            </GradientText>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Share Revelata with your network and earn rewards
            </Typography>
            {/* Snackbar for error and status messages */}
            <Box sx={{ 
            backgroundColor: `${brand[900]}40`,
            border: `1px solid ${gray[300]}80`,
            color: 'white',
            p: 0,
            borderRadius: 1,
            textAlign: 'center',
            mx: 'auto',
            mb: openSnackbar ? 3 : 0,
            zIndex: 1,
            width: '50%',
            maxWidth: '600px',
            opacity: openSnackbar ? 1 : 0,
            height: openSnackbar ? '48px' : 0,
            overflow: 'hidden',
            transition: 'all 0.5s ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
            }}>
            {snackbarMessage}
            </Box>
        </Box>

        {/* Show skeletons while we're loading initial state... */}
        {loading ? (
            <Box sx={{ width: '100%', maxWidth: 'md', mx: 'auto' }}>
            {/* Title skeleton */}
            <Skeleton variant="text" width={320} height={56} sx={{ mx: 'auto', mb: 2 }} />
            <Grid container spacing={4}>
                {/* Program description skeleton */}
                <Grid item xs={12} md={3.5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height={120} />
                </Box>
                </Grid>
                {/* Eligible rewards skeleton */}
                <Grid item xs={12} md={5}>
                <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 2 }} />
                </Grid>
                {/* Referral codes skeleton */}
                <Grid item xs={12} md={3.5}>
                <Box sx={{ maxWidth: '350px', mx: 'auto' }}>
                    <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
                    {/* Simulate 3 code cards */}
                    {[1,2,3].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" width="100%" height={72} sx={{ mb: 2, borderRadius: 2 }} />
                    ))}
                </Box>
                </Grid>
            </Grid>
            </Box>
        ) : (

            <>
            {/* Primary content: Description on the left, Eligible rewards in the center, referral codes on the right */}
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 2, md: 6, lg: 9 }}>
                {/* Program description */}
                <Box flex={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, height: '100%', color: 'text.secondary', minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3, mb:5}}>
                        How our rewards work
                    </Typography>
                    <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 1, padding: 2, minHeight: '297px', width: '100%' }}>
                        <ul style={{ margin: 0, paddingLeft: 20}}>
                        <li style={{ marginBottom: 24 }}>
                            <Typography variant="body2">
                            Refer a friend using the codes or links provided. <span style={{ color: '#2196F3', fontWeight: 'bold' }}> They'll earn one month free</span> when they purchase a subscription.
                            </Typography>
                        </li>
                        <li style={{ marginBottom: 24 }}>
                            <Typography variant="body2">
                            When three referrals purchase a subscription, <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>you will rearn earn one month free too!</span>
                            </Typography>
                        </li>
                        </ul>
                    </Box>     
                </Box>

                {/* Eligible rewards panel */}
                <Box flex={1} sx={{ minWidth: '350px' }}>
                    <ReferralCodeCard claimed={false}>
                        <CardContent>
                        {/* Eligible rewards header */}
                        <Box sx={{ display: 'flex', alignItems: 'top', gap: 2, mb: 3}}>
                            <EmojiEventsIcon color="primary" sx={{ fontSize: 40 }} /> {/* Trophy icon */}
                            <Typography variant="h5">
                            Eligible Rewards
                            </Typography>
                        </Box>
                        {/* Pills listing eligible rewards. */}
                        <RewardBox>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', minHeight: '200px', mt: 3 }}>
                            {/* Pill List */}                      
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                {/* Invitiation Code Reward -- Pill */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'center' }}>                       
                                    <RewardPill
                                    sx={{
                                        width: '280px',
                                        fontWeight: 600,
                                        background: hasClaimedReferralReward
                                        ? 'linear-gradient(135deg, rgba(0, 188, 212, 0.3) 0%, rgba(0, 229, 255, 0.3) 100%)'
                                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(33, 150, 243, 0.3) 100%)',
                                        border: hasClaimedReferralReward ? `1px solid ${brand[300]}` : '1px solid rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        cursor: !hasClaimedReferralReward ? 'pointer' : 'default',
                                    }}
                                    label={
                                        claimLoading
                                        ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                                <SketchedLogoSpinner size={18} sx={{ mr: 1 }} />
                                                Claiming...
                                            </Box>
                                            )
                                        : (
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Invitation Code: Get 1 Month Free
                                            </Typography>
                                        )
                                    }
                                    iseligible={!hasClaimedReferralReward}
                                    onClick={!hasClaimedReferralReward && !claimLoading ? () => setOpenClaimDialog(true) : undefined}
                                    />
                                </Box>
                                {/* Invitiation Code Reward -- Status text */}
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', m: -1.5, p: 0, mb: 1 }}>
                                    {hasClaimedReferralReward ? (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                        color: `${brand[300]}`,
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        lineHeight: 1.5,                                        
                                        textAlign: 'center',
                                        m: 0,
                                        p: 0,
                                        }}
                                    >
                                        Claimed! Automatically redeemed when you subscribe.
                                    </Typography>
                                    ) : (
                                    <Link
                                        component="button"
                                        variant="caption"
                                        onClick={() => setOpenClaimDialog(true)}
                                        sx={{ 
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                        minWidth: '40px'  // Ensure consistent spacing
                                        }}
                                    >
                                        Click to manually enter an invitation code
                                    </Link>
                                    )}
                                </Box>
                                {/* Referrer Code Reward -- Pill */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt:1.5 }}>
                                    <RewardPill 
                                    label={
                                        isRedeeming
                                        ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                                <SketchedLogoSpinner size={18} sx={{ mr: 1 }} />
                                                Redeeming...
                                            </Box>
                                            )
                                        : `Refer 3 Friends: Get 1 Month Free`
                                    }
                                    sx={{
                                        width: '280px',
                                        fontWeight: 600,
                                        background: 
                                        hasClaimedReferrerReward ? 'linear-gradient(135deg, rgba(0, 188, 212, 0.3) 0%, rgba(0, 229, 255, 0.3) 100%)'
                                        : isEligibleForReferralReward ? 'linear-gradient(135deg, rgba(140, 213, 143, 0.3) 0%, rgba(33, 149, 243, 0.89) 100%)'
                                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(33, 150, 243, 0.3) 100%)',
                                    border: hasClaimedReferrerReward
                                        ? `1px solid ${brand[300]}` 
                                        : '1px solid rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                    }}
                                    iseligible={isEligibleForReferralReward && !hasClaimedReferrerReward}
                                    onClick={!hasClaimedReferrerReward && !isRedeeming ? handleRedeemReferrerCoupon : undefined}
                                    />
                                </Box>
                                {/* Referrer Code Reward -- Status Text */}
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: -1.5, p: 0 }}>
                                    {isEligibleForReferralReward ? (
                                        hasClaimedReferrerReward ? (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                            color: `${brand[300]}`,
                                            fontWeight: 700,
                                            fontSize: '0.72rem',
                                            lineHeight: 1.5,
                                            textAlign: 'center',
                                            m: 0,
                                            p: 0,
                                            }}
                                        >
                                            Claimed! Automatically redeemed and applied to your subscription.
                                        </Typography>
                                        ) : (
                                        <Link
                                            component="button"
                                            variant="caption"
                                            onClick={handleRedeemReferrerCoupon}
                                            sx={{ 
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' },
                                            color: 'primary.main',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            }}
                                        >
                                            Unlocked! Click to claim.
                                        </Link>
                                        )
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LockOutlinedIcon fontSize="caption" sx={{ color: 'text.secondary' }} />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            fontSize: '0.72rem',
                                            }}
                                        >
                                            Unlock when three referrals subscribe
                                        </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            </Box>
                        </RewardBox>                  
                        </CardContent>
                    </ReferralCodeCard>
                </Box>

                {/* Referral codes panel */}
                <Box flex={1} sx={{ maxWidth: '350px', margin: { xs: '0 auto', md: '0 auto' }, alignSelf: 'flex-start', minWidth: 0 }}>
                    {/* Progress section: show how many referral cards have been claimed and redeemed */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3 }}>
                        <LockOutlinedIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} /> Progress to Referral Reward
                        </Typography>
                        {/* Progress bar for redeemed coupons */}
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flexGrow: 1.2, ml: 2 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={Math.min((coupons.filter(c => c.redeemed_at).length / 3) * 100, 100)} 
                                        sx={{ height: 8, borderRadius: 5 }}
                                    />
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 48, textAlign: 'center' }}>
                                    {`${coupons.filter(c => c.redeemed_at).length} / 3`}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* A button to generate referral codes if there are no codes: */}
                    <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 1, padding: 1, minHeight: '298px', minWidth: '100%' }}>
                        {/* Always show code cards if any exist */}
                        {coupons.filter(coupon => coupon.campaign_id === process.env.REACT_APP_REFERRAL_CAMPAIGN_ID).length > 0 && (
                            <Box 
                                sx={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    maxHeight: 240, 
                                    overflowY: 'auto', 
                                    pr: 1 
                                }}
                            >
                                {coupons
                                    .filter(coupon => coupon.campaign_id === process.env.REACT_APP_REFERRAL_CAMPAIGN_ID)
                                    .map((coupon) => (
                                        <Grid item xs={12} key={coupon.code}>
                                            <ReferralCodeCard claimed={!!coupon.claimed_by_sub} redeemed={!!coupon.redeemed_at}>
                                                <CardContent sx={{ p: 1, mb: -2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Code:
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ 
                                                                fontFamily: 'monospace',
                                                                fontSize: '0.9rem',
                                                                letterSpacing: '0.1em'
                                                            }}
                                                        >
                                                            {coupon.code}
                                                        </Typography>
                                                        {(coupon.redeemed_at || coupon.claimed_by_sub) && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0}}>
                                                                ({coupon.redeemed_at ? 'Redeemed' : 'Claimed'})
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={`${window.location.origin}/signup?invitation_code=${coupon.code}`}
                                                            InputProps={{
                                                                readOnly: true,
                                                                sx: { 
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.75rem',
                                                                    '& .MuiInputBase-input': {
                                                                        py: 0.5,
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <Tooltip title="Copy Link">
                                                            <IconButton 
                                                                onClick={() => handleCopyLink(coupon.code)} 
                                                                color="primary" 
                                                                size="small"
                                                                disabled={coupon.claimed_by_sub || coupon.redeemed_at}
                                                                sx={{ p: 0.5, mx: 0 }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Share">
                                                            <IconButton 
                                                                onClick={() => handleShare(coupon.code)} 
                                                                color="primary" 
                                                                size="small"
                                                                disabled={coupon.claimed_by_sub || coupon.redeemed_at}
                                                                sx={{ p: 0.5, mx: 0 }}
                                                            >
                                                                <ShareIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </CardContent>
                                            </ReferralCodeCard>
                                        </Grid>
                                    ))}
                            </Box>
                        )}
                                                {/* Show the button if fewer than 100 codes */}
                                                {coupons.length < 100 && (
                            <Box sx={{ textAlign: 'center', my: -2, alignSelf: 'flex-start' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleGenerateReferralCodes}
                                    disabled={generating}
                                    sx={{ mb: 2, fontWeight: 600, mt:3 }}
                                >
                                    {generating ? 'Generating...' : 'Get More Referral Codes'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            </>
        )}

        <Box sx={{ mt: 12, textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography 
            variant="caption" 
            sx={{     
                fontSize: '0.65rem',
                lineHeight: 1.2,
                display: 'block',
                color: 'rgba(129, 121, 121, 0.75)'  // Darker text for light mode
            }}
            >
            Referral Rewards Terms & Conditions: Limit one invitation coupon per customer. Referrer-coupons are granted when all three referral codes have been claimed and then redeemed at checkout for qualifying purchases. Limit one referrer coupon per customer. All referrer coupons are non-transferrable. Program is open to existing customers in good standing. Referrers and their referred customers must be different individuals with different payment methods and shipping addresses. Coupons cannot be combined with other promotional offers and apply only to the Launch Preview Subscription. Any attempt to manipulate the program through multiple accounts or automated systems will result in disqualification. Revelata reserves the right to modify, suspend, or terminate this program at any time without prior notice, and to determine eligibility or deny participation at its sole discretion.
            </Typography>
        </Box>

        <Dialog open={openClaimDialog} onClose={() => !claimLoading && handleCloseClaimDialog()}>
            <DialogTitle>Claim Invitation Code</DialogTitle>
            <DialogContent>
            <TextField
                autoFocus
                margin="dense"
                label="Invitation Code"
                fullWidth
                variant="outlined"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value)}
                disabled={claimLoading}
            />
            </DialogContent>
            <DialogActions>
            <Button 
                onClick={handleCloseClaimDialog} 
                disabled={claimLoading}
            >
                Cancel
            </Button>
            <Button 
                onClick={handleClaimCode} 
                variant="contained" 
                disabled={claimLoading}
                sx={{ 
                minWidth: '120px',
                height: '40px',
                px: 3,
                position: 'relative'
                }}
            >
                {claimLoading ? (
                <Box sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                }}>
                    <SketchedLogoSpinner size={32} />
                </Box>
                ) : 'Claim'}
            </Button>
            </DialogActions>
        </Dialog>
        </Container>
    );
}

export default Coupon; 